import React, { useState } from "react";
import { Button, Dialog, Typography } from "@mui/material";
import styled from "styled-components";
import { FlowEditContext } from "../../FlowEditContext";
import { FlowConfiguration } from "../../../rule-engine-core/Flow/FlowConfiguration";
import { SearchGrayIcon } from "../../../CommonUI/Icon/SearchGrayIcon";
import { FlowStepEditorRegistration, FlowStepEditorRegistrationData } from "../../Utilities/RegisterFlowEditor";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { CloseGrayIcon } from "../../../CommonUI/Icon/CloseGrayIcon";
import { TextField } from "@mui/material";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

interface ToolsPickerProps {
    flow: FlowConfiguration;
    editContext: FlowEditContext;
    open: boolean;
    onClose: () => void;
}

export const ToolsPicker: React.FC<ToolsPickerProps> = (props: ToolsPickerProps) => {
    const [searchStep, setSearchStep] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedSteps, setSelectedSteps] = useState<string[]>([]);
    const [removedSteps, setRemovedSteps] = useState<string[]>([]);

    const itemsByCategories = FlowStepEditorRegistration.getItemsByCategories();

    const onChangeSearchStep = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchStep(event.target.value);
    };

    const onApply = () => {
        props.flow.visibleSteps = [
            ...selectedSteps,
            ...props.flow.visibleSteps.filter((stepName) => !removedSteps.includes(stepName)),
        ];

        setSelectedSteps([]);
        setRemovedSteps([]);

        StateManager.propertyChanged(props.flow, FlowConfiguration.VISIBLE_STEPS);
        props.onClose();
    };

    const onAddAll = () => {
        // eslint-disable-next-line
        let tempSteps = Object.entries(itemsByCategories).reduce((list: string[], [_, items]) => {
            list = [
                ...list,
                ...items.filter((item) => !props.flow.visibleSteps.includes(item.name)).map((item) => item.name),
            ];
            return list;
        }, []);

        setRemovedSteps([]);
        setSelectedSteps(tempSteps);
    };

    const filteredItems = Object.entries(itemsByCategories).reduce(
        (obj: { [key: string]: FlowStepEditorRegistrationData[] }, [category, items]) => {
            if (searchStep) {
                if (category.toLowerCase().includes(searchStep.toLowerCase())) {
                    obj[category] = items;
                } else {
                    obj[category] = items.filter((item) => item.name.toLowerCase().includes(searchStep.toLowerCase()));
                }
            }
            return obj;
        },
        {},
    );

    const onSelectedSteps = (stepName: string) => {
        if (props.flow.visibleSteps.includes(stepName)) {
            let idx = removedSteps.indexOf(stepName);
            let tempSteps = [...removedSteps];

            if (idx >= 0) {
                tempSteps.splice(idx, 1);
            } else {
                tempSteps.push(stepName);
            }
            setRemovedSteps(tempSteps);
        } else {
            let idx = selectedSteps.indexOf(stepName);
            let tempSteps = [...selectedSteps];
            if (idx < 0) {
                tempSteps = [...tempSteps, stepName];
            } else {
                tempSteps.splice(idx, 1);
            }
            setSelectedSteps(tempSteps);
        }
    };

    return (
        <StyledDialog
            open={props.open}
            onClose={props.onClose}
            data-testid="ToolsPicker__dialog"
        >
            <div className="ToolsPicker__header">
                <div className="ToolsPicker__header__item">
                    <Typography variant="subtitle1">Pick Step</Typography>
                </div>
                <div className="ToolsPicker__header__item">
                    <label>Toolbox:</label>
                    <span>{props.flow.visibleSteps.length} steps</span>
                </div>
                <div className="ToolsPicker__header__item">
                    <label>Chosen:</label>
                    <span>{selectedSteps.length} steps</span>
                </div>
                <div className="ToolsPicker__header__item ToolsPicker__search">
                    <ToolboxSearch
                        placeholder="Search"
                        variant="outlined"
                        value={searchStep}
                        onChange={onChangeSearchStep}
                        data-testid={"ToolsPicker_text_search"}
                        InputProps={{
                            endAdornment: searchStep && searchStep !== "" ? <CloseGrayIcon /> : <SearchGrayIcon />,
                        }}
                    />
                </div>
            </div>
            <div className="ToolsPicker__body">
                {(!searchStep || searchStep === "") && (
                    <>
                        <div className="ToolsPicker__body__category">
                            <Typography variant="subtitle1">Categories</Typography>
                            <div className="Categories__root">
                                <div className="Categories__list">
                                    {Object.keys(itemsByCategories).map((category) => {
                                        return (
                                            <div
                                                className={`Categories__list__item${
                                                    selectedCategory === category ? " Selected" : ""
                                                }`}
                                                key={category}
                                                onClick={() => setSelectedCategory(category)}
                                            >
                                                {category}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="ToolsPicker__body__category__steps">
                            <div className="Steps__root">
                                <div className="Steps__grid">
                                    {selectedCategory !== null &&
                                        itemsByCategories[selectedCategory].map((step) => {
                                            return (
                                                <StepItem
                                                    step={step}
                                                    selectedSteps={selectedSteps}
                                                    removedSteps={removedSteps}
                                                    onSelectedSteps={onSelectedSteps}
                                                    flow={props.flow}
                                                    key={step.name}
                                                />
                                            );
                                        })}
                                </div>
                            </div>
                            <div className="Steps__select_all">
                                <Button onClick={onAddAll}>Select all tools</Button>
                            </div>
                        </div>
                    </>
                )}
                {searchStep && searchStep !== "" && (
                    <div className="SearchResults">
                        <div className="Categories">
                            {Object.entries(filteredItems).map(([category, items]) => {
                                if (items.length === 0) {
                                    return null;
                                } else {
                                    return (
                                        <div
                                            className="Category__root"
                                            key={category}
                                        >
                                            <div className="Category__name">{category}</div>
                                            <div className="Steps__grid">
                                                {items.map((step) => (
                                                    <StepItem
                                                        step={step}
                                                        key={step.name}
                                                        flow={props.flow}
                                                        selectedSteps={selectedSteps}
                                                        removedSteps={removedSteps}
                                                        onSelectedSteps={onSelectedSteps}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                        </div>

                        <div className="Steps__select_all">
                            <Button onClick={onAddAll}>Select all tools</Button>
                        </div>
                    </div>
                )}
            </div>
            <div className="ToolsPicker__actions">
                <Button
                    variant="contained"
                    onClick={onApply}
                    data-testid="btn-add-to-toolbox"
                >
                    Add to My toolbox
                </Button>
            </div>
        </StyledDialog>
    );
};

interface IStepItem {
    step: FlowStepEditorRegistrationData;
    flow: FlowConfiguration;
    selectedSteps: string[];
    removedSteps: string[];
    onSelectedSteps: (stepName: string) => void;
}

const StepItem: React.FC<IStepItem> = (props: IStepItem) => {
    const { step, onSelectedSteps, selectedSteps, removedSteps } = props;

    const isAdded = props.flow.visibleSteps.includes(step.name) && !removedSteps.includes(step.name);
    const isSelected = selectedSteps.includes(step.name);

    return (
        <StepItemDiv
            className={`Steps__item ${isSelected ? "Selected" : ""} ${isAdded ? "Added" : ""}`}
            onClick={() => onSelectedSteps(step.name)}
            style={{
                margin: "5px",
                background: isAdded || isSelected ? UIStyleConstants.BLUE_VERY_LIGHT_COLOR : "white",
            }}
        >
            <div className="Steps__item__icon">{step.icon}</div>
            <div className="Steps__item__name">{step.name}</div>
        </StepItemDiv>
    );
};

const StyledDialog = styled(Dialog)`
  .MuiDialog-paperScrollPaper {
    padding: 16px 20px;
  }

  .MuiDialog-paperWidthSm {
    max-width: none;
  }

  *::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 2px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    background-color: transparent;
  }

  *::-webkit-scrollbar {
    width: 8px;
    background-color: transparent;
  }

  *::-webkit-scrollbar-thumb {
    background-color: ${UIStyleConstants.BLUE_COLOR};
  }

  .ToolsPicker__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 16px;

    .MuiTypography-subtitle1 {
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 100%;
      color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    }

    .ToolsPicker__header__item {
      margin-right: 15px;

      &.ToolsPicker__search {
        margin-left: auto;
      }

      label {
        font-style: normal;
        font-weight: normal;
        font-size: 9px;
        line-height: 160%;
        color ${UIStyleConstants.GRAY_COLOR};;
        margin-right: 4px;
      }

      span {
        font-style: normal;
        font-weight: normal;
        font-size: 9px;
        line-height: 160%;
        color ${UIStyleConstants.GRAY_COLOR};;
      }
    }
  }

  .ToolsPicker__actions {
    padding: 16px 0 0;
    display: flex;
    justify-content: flex-end;

    .MuiButton-contained {
      background: ${UIStyleConstants.BLUE_COLOR};
      color: ${UIStyleConstants.WHITE_COLOR};

      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 140%;
      text-transform: none;
      padding: 6px 16px;
    }
  }

  .ToolsPicker__body {
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow: auto;
    border-bottom: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    min-height: 286px;

    .SearchResults {
      display: flex;
      flex-direction: column;
      overflow: auto;

      .Categories {
        flex: 1;
        overflow: auto;
        max-width: 612px;
        min-width: 612px;
      }

      .Steps__grid {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;

        > div {
          width: 25%;
        }
      }
    }

    .Steps__select_all {
      display: flex;
      justify-content: flex-end;
      padding: 14px 0;

      .MuiButton-text {
        padding: 0;

        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 140%;
        color: ${UIStyleConstants.BLUE_COLOR};
        text-transform: none;

        &:hover {
          background-color: transparent;
          color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        }
      }
    }

    .MuiTypography-subtitle1 {
      font-style: normal;
      font-weight: 500;
      font-size: 10px;
      line-height: 12px;
      display: flex;
      align-items: center;
      letter-spacing: 0.2px;
      color: ${UIStyleConstants.BLUE_COLOR};
      margin-bottom: 8px;
    }

    .ToolsPicker__body__category {
      min-width: 156px;
      border-right: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
      padding-right: 4px;
      display: flex;
      flex-direction: column;
      margin-bottom: 16px;

      .Categories__root {
        flex: 1;
        overflow: auto;
        padding-right: 2px;

        .Categories__list {
          background: ${UIStyleConstants.GRAY_LIGHTER_COLOR};
          border-radius: 4px;
          padding: 2px;

          .Categories__list__item {
            cursor: pointer;

            font-style: normal;
            font-weight: 500;
            font-size: 11px;
            line-height: 13px;
            color: ${UIStyleConstants.BLUE_COLOR};
            padding: 8px 14px;
            border-radius: 4px;

            &.Selected {
              color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
              background: ${UIStyleConstants.WHITE_COLOR};
            }
          }
        }
      }
    }

    .ToolsPicker__body__category__steps {
      min-width: 500px;
      max-width: 500px;
      display: flex;
      flex-direction: column;

      .Steps__root {
        flex: 1;
        overflow: auto;

        .Steps__grid {
          display: flex;
          flex-wrap: wrap;
          padding: 8px;

          > div {
            width: 31.5%;
          }
        }
      }
    }
  }
`;

const StepItemDiv = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 8px;

    .Steps__item__icon {
        width: 38px;
        height: 32px;
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 8px;
    }

    .Steps__item__name {
        font-style: normal;
        font-weight: normal;
        font-size: 11px;
        line-height: 140%;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    }

    &.Selected {
        .Steps__item__icon {
            background: ${UIStyleConstants.WHITE_COLOR};
            border: 1px solid ${UIStyleConstants.BLUE_COLOR};
        }

        .Steps__item__name {
            color: ${UIStyleConstants.BLUE_COLOR};
        }
    }

    &.Added {
        .Steps__item__icon {
            background: ${UIStyleConstants.WHITE_COLOR};
        }
    }
`;

export const ToolboxSearch = styled(TextField)`
    .MuiOutlinedInput-input {
        padding: 8px 0 8px 16px;
    }

    .MuiOutlinedInput-adornedEnd {
        padding-right: 12px;
    }

    .MuiInputBase-root {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 140%;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        &.Mui-focused {
            .MuiOutlinedInput-notchedOutline {
                border-color: ${UIStyleConstants.GRAY_LIGHTER_COLOR};
            }
        }
    }

    .MuiInputBase-input::placeholder {
        color: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    }
`;
